import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Stack,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
  Chip,
  Pagination,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { ordersApi } from '../../utils/apiClient';
import { useToast } from '../../hooks/useToast';

const OrderStatusOptions = [
  'received', 'won', 'bought', 'processing', 'shipped', 'delivered', 'failed', 'refunded', 'cancelled',
];

const STATUS_COLOR = {
  received:   'default',
  won:        'success',
  bought:     'success',
  processing: 'info',
  shipped:    'info',
  delivered:  'success',
  failed:     'error',
  refunded:   'warning',
  cancelled:  'error',
};

const AdminOrders = ({
  orders,
  ordersLoading,
  ordersError,
  ordersPage,
  ordersTotalPages,
  fetchOrders,
  openOrderModal,
  showOrderModal,
  selectedOrder,
  closeOrderModal,
}) => {
  const toast = useToast();
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [localStatus, setLocalStatus] = useState('');
  const [filters, setFilters] = useState({ search: '', status: 'all', source: 'all', limit: 10 });
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    if (selectedOrder) setLocalStatus(selectedOrder.status || 'received');
  }, [selectedOrder]);

  const handleStatusChange = async (orderId) => {
    if (!selectedOrder) return;
    setStatusUpdating(true);
    try {
      await ordersApi.updateStatus(orderId, { status: localStatus });
      fetchOrders(ordersPage, {
        status: filters.status === 'all' ? undefined : filters.status,
        source: filters.source === 'all' ? undefined : filters.source,
        search: filters.search,
        limit: filters.limit,
      });
      closeOrderModal();
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleSearch = () => {
    fetchOrders(1, {
      status: filters.status === 'all' ? undefined : filters.status,
      source: filters.source === 'all' ? undefined : filters.source,
      search: filters.search,
      limit: filters.limit,
    });
  };

  const openOrderDetails = async (order) => {
    try {
      setLoadingDetails(true);
      const res = await ordersApi.getById(order._id);
      openOrderModal(res.data || order);
    } catch (err) {
      openOrderModal(order);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handlePageChange = (_, page) => {
    fetchOrders(page, {
      status: filters.status === 'all' ? undefined : filters.status,
      source: filters.source === 'all' ? undefined : filters.source,
      search: filters.search,
      limit: filters.limit,
    });
  };

  return (
    <>
      <Card sx={{ borderRadius: 3, boxShadow: '0px 16px 45px rgba(15, 23, 42, 0.12)' }}>
        <CardHeader
          title="Orders"
          subheader="Track and manage customer orders from all sources"
          titleTypographyProps={{ variant: 'h5', fontWeight: 700 }}
        />
        {ordersLoading && <LinearProgress color="primary" />}
        <CardContent sx={{ pt: 0 }}>
          {ordersError && <Alert severity="error" sx={{ mb: 2 }}>{ordersError}</Alert>}

          {/* Filters */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel id="filter-status-label">Status</InputLabel>
              <Select labelId="filter-status-label" value={filters.status} label="Status"
                onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}>
                <MenuItem value="all">All</MenuItem>
                {OrderStatusOptions.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel id="filter-source-label">Source</InputLabel>
              <Select labelId="filter-source-label" value={filters.source} label="Source"
                onChange={(e) => setFilters(f => ({ ...f, source: e.target.value }))}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="ecommerce">Ecommerce</MenuItem>
                <MenuItem value="auction">Auction</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Search by user or order ID"
              value={filters.search}
              onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              sx={{ minWidth: 220 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleSearch}><SearchIcon fontSize="small" /></IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="outlined" startIcon={<RefreshOutlinedIcon />} onClick={() => fetchOrders(1)} size="small">
              Refresh
            </Button>
          </Stack>

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 780 }}>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' } }}>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(!orders || !orders.length) && !ordersLoading ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography color="text.secondary" align="center" sx={{ py: 4 }}>No orders found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  (orders || []).map((o) => (
                    <TableRow key={o._id} hover sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'text.secondary' }}>
                          {o._id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {o.userId?.username || o.user?.username || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={o.items?.[0]?.source || 'ecommerce'} size="small" sx={{ borderRadius: 1.5, fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>
                        {(o.items || []).slice(0, 2).map((it, idx) => (
                          <Typography key={it._id || it.productId || idx} variant="caption" display="block">
                            {it.title || it.name || 'Item'}{it.bidAmount ? ` (bid: ${it.bidAmount})` : ''}
                          </Typography>
                        ))}
                        {(o.items || []).length > 2 && (
                          <Typography variant="caption" color="text.secondary">+{(o.items || []).length - 2} more</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{o.totalPrice ?? o.total ?? 'N/A'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={o.status || 'N/A'}
                          color={STATUS_COLOR[o.status] || 'default'}
                          size="small"
                          sx={{ fontWeight: 600, borderRadius: 1.5, textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {o.createdAt ? format(new Date(o.createdAt), 'yyyy-MM-dd HH:mm') : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="text" onClick={() => openOrderDetails(o)} disabled={loadingDetails}>
                          {loadingDetails ? <CircularProgress size={16} /> : 'View'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {ordersTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination count={ordersTotalPages} page={ordersPage} onChange={handlePageChange} color="primary" shape="rounded" />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Order Details Dialog */}
      <Dialog
        open={!!(showOrderModal && selectedOrder)}
        onClose={closeOrderModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ReceiptLongOutlinedIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>Order Details</Typography>
          </Stack>
          <IconButton size="small" onClick={closeOrderModal}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <Divider />
        {selectedOrder && (
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              <Typography variant="body2"><strong>Order ID:</strong> <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{selectedOrder._id}</span></Typography>
              <Typography variant="body2"><strong>User:</strong> {selectedOrder.userId?.username || selectedOrder.user?.username || 'N/A'}</Typography>
              <Typography variant="body2"><strong>Total:</strong> {selectedOrder.totalPrice ?? selectedOrder.total ?? 'N/A'}</Typography>
            </Stack>

            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Items</Typography>
            <Stack spacing={1} sx={{ mb: 3 }}>
              {(selectedOrder.items || []).map((it, idx) => (
                <Box key={it._id || it.productId || idx} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                  <Typography variant="body2" fontWeight={500}>{it.title || it.name || 'Item'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Price: {it.price} &bull; Qty: {it.quantity || 1} &bull; Source: {it.source}
                    {it.bidAmount && ` • Bid: ${it.bidAmount}`}
                  </Typography>
                </Box>
              ))}
            </Stack>

            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Update Status</Typography>
            <FormControl fullWidth size="small">
              <InputLabel id="status-select-label">Status</InputLabel>
              <Select labelId="status-select-label" value={localStatus} label="Status" onChange={(e) => setLocalStatus(e.target.value)}>
                {OrderStatusOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={closeOrderModal}>Cancel</Button>
          <Button variant="contained" onClick={() => handleStatusChange(selectedOrder?._id)} disabled={statusUpdating}>
            {statusUpdating ? 'Updating…' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

AdminOrders.propTypes = {
  orders: PropTypes.array,
  ordersLoading: PropTypes.bool,
  ordersError: PropTypes.string,
  ordersPage: PropTypes.number,
  ordersTotalPages: PropTypes.number,
  fetchOrders: PropTypes.func.isRequired,
  openOrderModal: PropTypes.func.isRequired,
  closeOrderModal: PropTypes.func.isRequired,
  showOrderModal: PropTypes.bool,
  selectedOrder: PropTypes.object,
};

export default AdminOrders;
